import { useEffect, useState, useMemo, useCallback } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  Funnel, FileXls, FilePdf, MagnifyingGlass, X, Phone, MapPin, Buildings, Tag, CircleNotch, NotePencil, Check,
} from '@phosphor-icons/react';

const CLIENT_TYPE_LABELS = {
  proyecto: 'Proyecto', vidrieria: 'Vidriería', instalador: 'Instalador',
  constructora: 'Constructora', cliente_final: 'Cliente final', otro: 'Otro',
};

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => iso ? iso.slice(0, 10) : '—';

const ClientesVendedores = () => {
  const [filters, setFilters] = useState({ q: '', empresa: '', client_type: '', branch: '', user_id: '', has_address: '', departamento: '' });
  const [clients, setClients] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({ xls: false, pdf: false });
  const [editing, setEditing] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) p.set(k, v); });
    return p;
  }, [filters]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clientes/vendedores?${params.toString()}`);
      setClients(r.data.clients || []);
      if (r.data.departamentos) setDepartamentos(r.data.departamentos);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando clientes');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    (async () => {
      try {
        const [v, b] = await Promise.all([
          api.get('/work-plan/dashboard/team?days=30'),
          api.get('/work-plan/branches'),
        ]);
        setVendors((v.data?.users || v.data?.team || []).map(x => ({ id: x.user_id || x.id, name: x.name })));
        setBranches(b.data?.branches || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const exportFile = async (kind) => {
    setExporting(prev => ({ ...prev, [kind]: true }));
    try {
      const url = kind === 'pdf' ? '/clientes/vendedores/export/pdf' : '/clientes/vendedores/export/excel';
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const r = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: mime });
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `clientes-vendedores-${Date.now()}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch { toast.error('Error exportando'); }
    finally { setExporting(prev => ({ ...prev, [kind]: false })); }
  };

  const clearFilters = () => setFilters({ q: '', empresa: '', client_type: '', branch: '', user_id: '', has_address: '', departamento: '' });

  return (
    <div className="p-4 lg:p-6 space-y-5 min-h-screen bg-[var(--bg)]">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-[var(--text)] flex items-center gap-2">
            <Buildings size={22} weight="bold" className="text-[#1D4ED8]" /> Clientes de Vendedores
          </h1>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">Capturados en Plan de Trabajo (llamadas, visitas, cotizaciones, ventas). Deduplicados por teléfono.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportFile('pdf')} disabled={exporting.pdf || loading}
            className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="export-pdf">
            {exporting.pdf ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="bold" />} PDF
          </button>
          <button onClick={() => exportFile('xls')} disabled={exporting.xls || loading}
            className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="export-clientes-vendedores">
            {exporting.xls ? <CircleNotch size={14} className="animate-spin" /> : <FileXls size={14} weight="bold" />} Excel
          </button>
        </div>
      </header>

      <section className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)] flex items-center gap-1.5"><Funnel size={14} />Filtros</h3>
          <button onClick={clearFilters} className="text-[10px] uppercase tracking-wider font-bold text-[#DC2626] hover:underline">Limpiar</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Buscar (nombre/teléfono/empresa)</label>
            <div className="relative mt-1">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="..."
                className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
                data-testid="filter-q" />
            </div>
          </div>
          <Sel label="Empresa CYH/SA" val={filters.empresa} on={(v) => setFilters({ ...filters, empresa: v })} opts={[{ v: 'CYH', l: 'CYH' }, { v: 'SA', l: 'SA' }]} placeholder="Cualquiera" testid="filter-empresa" />
          <Sel label="Tipo cliente" val={filters.client_type} on={(v) => setFilters({ ...filters, client_type: v })} opts={Object.entries(CLIENT_TYPE_LABELS).map(([v, l]) => ({ v, l }))} placeholder="Cualquiera" testid="filter-ct" />
          <Sel label="Vendedor" val={filters.user_id} on={(v) => setFilters({ ...filters, user_id: v })} opts={vendors.map(v => ({ v: v.id, l: v.name }))} placeholder="Todos" testid="filter-vendor" />
          <Sel label="Sucursal" val={filters.branch} on={(v) => setFilters({ ...filters, branch: v })} opts={branches.map(b => ({ v: b, l: b }))} placeholder="Todas" testid="filter-branch" />
          <Sel label="Departamento" val={filters.departamento} on={(v) => setFilters({ ...filters, departamento: v })} opts={departamentos.map(d => ({ v: d, l: d }))} placeholder="Todos" testid="filter-dept" />
          <Sel label="Con dirección" val={filters.has_address} on={(v) => setFilters({ ...filters, has_address: v })} opts={[{ v: 'true', l: 'Sí' }, { v: 'false', l: 'No' }]} placeholder="Cualquiera" testid="filter-addr" />
        </div>
      </section>

      <section className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-hidden">
        <header className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <h3 className="text-sm font-black text-[var(--text)]">{clients.length} cliente(s) únicos</h3>
          {loading && <CircleNotch size={16} className="animate-spin text-[var(--text-muted)]" />}
        </header>
        {clients.length === 0 ? (
          <div className="p-10 text-center text-[var(--text-muted)] text-sm">{loading ? 'Cargando...' : 'Sin clientes con los filtros actuales'}</div>
        ) : (
          <>
            {/* Desktop: scrollable table */}
            <div className="hidden md:block overflow-x-auto -webkit-overflow-scrolling-touch">
              <table className="w-full text-xs" style={{ minWidth: '1000px' }}>
                <thead className="bg-[var(--bg)] text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                  <tr>
                    <th className="px-3 py-2 text-left">Cliente</th>
                    <th className="px-3 py-2 text-left">Teléfono</th>
                    <th className="px-3 py-2 text-left">Empresa/Tipo</th>
                    <th className="px-3 py-2 text-left">Depto.</th>
                    <th className="px-3 py-2 text-center">CYH/SA</th>
                    <th className="px-3 py-2 text-left">Vendedor</th>
                    <th className="px-3 py-2 text-center">Actividades</th>
                    <th className="px-3 py-2 text-right">Cotizado Q</th>
                    <th className="px-3 py-2 text-right">Vendido Q</th>
                    <th className="px-3 py-2 text-center">Última</th>
                    <th className="px-3 py-2 text-center">Editar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {clients.map(c => (
                    <tr key={c.phone} className="hover:bg-[var(--bg)]" data-testid={`vendor-client-${c.phone}`}>
                      <td className="px-3 py-2 text-[var(--text)] font-bold">{c.name || '—'}{c.company && <span className="block text-[10px] text-[var(--text-muted)] font-normal">{c.company}</span>}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <a href={`https://wa.me/${(c.phone || '').replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-[#1D4ED8] hover:underline">{c.phone}</a>
                      </td>
                      <td className="px-3 py-2 text-[var(--text-muted)]">
                        <span className="block">{c.client_type ? CLIENT_TYPE_LABELS[c.client_type] : '—'}</span>
                        {c.address && <span className="text-[10px] flex items-center gap-1"><MapPin size={10} />{c.address.slice(0, 30)}...</span>}
                      </td>
                      <td className="px-3 py-2 text-[var(--text)] text-[11px]">{c.departamento || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex justify-center gap-1">
                          {(c.empresas || []).map(e => (
                            <span key={e} className="text-[9px] font-black px-1.5 py-0.5 rounded bg-[#1D4ED8]/15 text-[#1D4ED8] border border-[#1D4ED8]/30">{e}</span>
                          ))}
                          {(c.empresas || []).length === 0 && <span className="text-[10px] text-[var(--text-faint)]">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[var(--text)]">{c.vendor_name}<span className="block text-[10px] text-[var(--text-muted)]">{c.branch}</span></td>
                      <td className="px-3 py-2 text-center text-[10px]">
                        <div className="inline-flex gap-1">
                          {c.calls > 0 && <span className="px-1 rounded bg-blue-500/20 text-blue-600">{c.calls}📞</span>}
                          {c.visits > 0 && <span className="px-1 rounded bg-amber-500/20 text-amber-600">{c.visits}📍</span>}
                          {c.quotes > 0 && <span className="px-1 rounded bg-purple-500/20 text-purple-600">{c.quotes}📄</span>}
                          {c.sales > 0 && <span className="px-1 rounded bg-emerald-500/20 text-emerald-600">{c.sales}💰</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-[var(--text)]">{c.total_quoted ? fmtQ(c.total_quoted) : '—'}</td>
                      <td className="px-3 py-2 text-right font-bold text-[var(--text)]">{c.total_sold ? fmtQ(c.total_sold) : '—'}</td>
                      <td className="px-3 py-2 text-center text-[10px] text-[var(--text-muted)]">{fmtDate(c.last_activity_at)}</td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => setEditing(c)} className="text-[#1D4ED8] hover:underline" data-testid={`edit-${c.phone}`}>
                          <NotePencil size={14} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards */}
            <div className="md:hidden divide-y divide-[var(--border)]">
              {clients.map(c => (
                <div key={c.phone} className="p-3 hover:bg-[var(--bg)]" data-testid={`vendor-client-card-${c.phone}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-black text-[var(--text)] text-sm truncate">{c.name || '—'}</p>
                      {c.company && <p className="text-[10px] text-[var(--text-muted)] truncate">{c.company}</p>}
                      <a href={`https://wa.me/${(c.phone || '').replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-[#1D4ED8] hover:underline text-xs font-bold flex items-center gap-1 mt-1">
                        <Phone size={11} weight="bold" />{c.phone}
                      </a>
                    </div>
                    <button onClick={() => setEditing(c)} className="text-[#1D4ED8] hover:underline shrink-0 p-1" data-testid={`edit-mobile-${c.phone}`}>
                      <NotePencil size={16} weight="bold" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                    {c.client_type && <span className="bg-[var(--surface-muted)] px-1.5 py-0.5 rounded text-[var(--text-muted)] font-bold">{CLIENT_TYPE_LABELS[c.client_type]}</span>}
                    {c.departamento && <span className="bg-amber-500/15 text-amber-700 px-1.5 py-0.5 rounded font-bold">📍 {c.departamento}</span>}
                    {(c.empresas || []).map(e => (
                      <span key={e} className="font-black px-1.5 py-0.5 rounded bg-[#1D4ED8]/15 text-[#1D4ED8] border border-[#1D4ED8]/30">{e}</span>
                    ))}
                  </div>
                  {c.address && <p className="text-[10px] text-[var(--text-muted)] mt-1 flex items-start gap-1"><MapPin size={10} className="shrink-0 mt-0.5" /><span>{c.address}</span></p>}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--border)]">
                    <div className="flex gap-1 text-[10px]">
                      {c.calls > 0 && <span className="px-1 rounded bg-blue-500/20 text-blue-600">{c.calls}📞</span>}
                      {c.visits > 0 && <span className="px-1 rounded bg-amber-500/20 text-amber-600">{c.visits}📍</span>}
                      {c.quotes > 0 && <span className="px-1 rounded bg-purple-500/20 text-purple-600">{c.quotes}📄</span>}
                      {c.sales > 0 && <span className="px-1 rounded bg-emerald-500/20 text-emerald-600">{c.sales}💰</span>}
                    </div>
                    <div className="text-right">
                      {c.total_quoted > 0 && <p className="text-[10px] font-bold text-purple-600">Cot {fmtQ(c.total_quoted)}</p>}
                      {c.total_sold > 0 && <p className="text-[10px] font-bold text-emerald-600">Ven {fmtQ(c.total_sold)}</p>}
                    </div>
                  </div>
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">Vendedor: <span className="font-bold text-[var(--text)]">{c.vendor_name}</span> · {fmtDate(c.last_activity_at)}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {editing && <EditMetaModal client={editing} departamentos={departamentos} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchClients(); }} />}
    </div>
  );
};

const Sel = ({ label, val, on, opts, placeholder, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">{label}</label>
    <select value={val} onChange={(e) => on(e.target.value)} className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" data-testid={testid}>
      <option value="">{placeholder}</option>
      {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const EditMetaModal = ({ client, departamentos = [], onClose, onSaved }) => {
  const [empresas, setEmpresas] = useState(client.empresas || []);
  const [tagsRaw, setTagsRaw] = useState((client.tags || []).join(', '));
  const [notes, setNotes] = useState(client.notes || '');
  const [departamento, setDepartamento] = useState(client.departamento || '');
  const [saving, setSaving] = useState(false);

  const toggleEmpresa = (e) => {
    setEmpresas(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clientes/vendedores/${encodeURIComponent(client.phone)}`, {
        empresas,
        tags: tagsRaw.split(',').map(t => t.trim()).filter(Boolean),
        notes,
        departamento,
      });
      toast.success('Cliente actualizado ✅');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-black text-[var(--text)]">Editar cliente</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18} /></button>
        </header>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Cliente</p>
            <p className="font-bold text-[var(--text)]">{client.name}</p>
            <p className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Phone size={10} /> {client.phone}</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Empresa(s) que aplica</label>
            <div className="flex gap-2 mt-1">
              {['CYH', 'SA'].map(e => (
                <button key={e} type="button" onClick={() => toggleEmpresa(e)}
                  className={`flex-1 px-3 py-2 rounded-md border text-xs font-black transition-colors ${empresas.includes(e) ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-[var(--bg)] text-[var(--text-muted)] border-[var(--border)]'}`}
                  data-testid={`empresa-toggle-${e}`}>
                  {empresas.includes(e) && <Check size={12} weight="bold" className="inline mr-1" />}{e}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">Multi-selección: puede aplicar a CYH, SA o ambas.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)] flex items-center gap-1"><MapPin size={10} />Departamento (sector)</label>
            <select value={departamento} onChange={(e) => setDepartamento(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
              data-testid="edit-dept">
              <option value="">(Sin asignar)</option>
              {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)] flex items-center gap-1"><Tag size={10} />Etiquetas (separadas por coma)</label>
            <input value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} placeholder="VIP, Zona Norte, Frecuente"
              className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
              data-testid="edit-tags" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Notas</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] resize-none"
              data-testid="edit-notes" />
          </div>
        </div>
        <div className="flex gap-2 px-5 py-4 border-t border-[var(--border)] bg-[var(--surface)]">
          <button onClick={onClose} className="flex-1 px-3 py-2 rounded-md border border-[var(--border)] text-xs font-bold text-[var(--text)] hover:bg-[var(--bg)]">Cancelar</button>
          <button onClick={save} disabled={saving} className="flex-1 px-3 py-2 rounded-md bg-[#1D4ED8] hover:bg-[#1E40AF] text-white text-xs font-bold disabled:opacity-50" data-testid="save-meta">
            {saving ? <CircleNotch size={14} className="animate-spin inline" /> : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientesVendedores;
